/**
 * Client-side role priority (mirrors backend rolePriority.js).
 */
function isTruthyFlag(val) {
  return val === true || val === 1 || val === '1';
}

function isAdmin(user) {
  return !!(user && isTruthyFlag(user.isAdmin));
}

function isDeliveryHead(user) {
  return !!(user && isTruthyFlag(user.isDeliveryHead));
}

export const ROLE_CODES = Object.freeze({
  ADMIN: 'admin',
  DH: 'dh',
  CSG: 'csg',
  PM: 'pm',
  RM: 'rm',
  EMPLOYEE: 'employee',
});

export const ROLE_PRIORITY = Object.freeze({
  [ROLE_CODES.ADMIN]: 1,
  [ROLE_CODES.DH]: 2,
  [ROLE_CODES.CSG]: 3,
  [ROLE_CODES.PM]: 4,
  [ROLE_CODES.RM]: 5,
  [ROLE_CODES.EMPLOYEE]: 6,
});

const ROLE_LABELS = Object.freeze({
  [ROLE_CODES.ADMIN]: 'Administrator',
  [ROLE_CODES.DH]: 'Delivery Head',
  [ROLE_CODES.CSG]: 'CSG',
  [ROLE_CODES.PM]: 'Project Manager',
  [ROLE_CODES.RM]: 'Reporting Manager',
  [ROLE_CODES.EMPLOYEE]: 'Employee',
});

function isCsgFlag(user) {
  return !!(user && isTruthyFlag(user.isCsg));
}

export function getAssignedRoles(user, ctx = {}) {
  const roles = [ROLE_CODES.EMPLOYEE];
  if (!user) return roles;

  if (isAdmin(user)) roles.push(ROLE_CODES.ADMIN);
  if (isDeliveryHead(user)) roles.push(ROLE_CODES.DH);
  if (isCsgFlag(user) && ctx.hasCsgProjects) roles.push(ROLE_CODES.CSG);
  if (user.isProjectManager) roles.push(ROLE_CODES.PM);
  if (ctx.isReportingManager || user.isReportingManager) roles.push(ROLE_CODES.RM);

  return [...new Set(roles)];
}

export function roleRank(roleCode) {
  return ROLE_PRIORITY[roleCode] ?? ROLE_PRIORITY[ROLE_CODES.EMPLOYEE];
}

export function getPrimaryRole(user, ctx = {}) {
  const assigned = getAssignedRoles(user, ctx);
  let primary = ROLE_CODES.EMPLOYEE;
  let bestRank = ROLE_PRIORITY[ROLE_CODES.EMPLOYEE];
  for (const code of assigned) {
    const r = roleRank(code);
    if (r < bestRank) {
      bestRank = r;
      primary = code;
    }
  }
  return primary;
}

export function hasAssignedRole(user, roleCode, ctx = {}) {
  return getAssignedRoles(user, ctx).includes(roleCode);
}

export function shouldApplyRoleRestriction(user, limitingRole) {
  if (!user) return false;
  const assigned =
    user.assignedRoles ||
    getAssignedRoles(user, {
      isReportingManager: user.isReportingManager,
      hasCsgProjects: user.hasCsgProjects,
    });
  const limitRank = roleRank(limitingRole);
  for (const code of assigned) {
    if (code === limitingRole) continue;
    if (roleRank(code) < limitRank) return false;
  }
  return assigned.includes(limitingRole);
}

export function getUserRoleLabelFromPrimary(primaryRole) {
  return ROLE_LABELS[primaryRole] || ROLE_LABELS[ROLE_CODES.EMPLOYEE];
}

/** Display label from user flags and optional context (matches backend getUserRoleLabel). */
export function getUserRoleLabel(user, ctx = {}) {
  return getUserRoleLabelFromPrimary(getPrimaryRole(user, ctx));
}

export function getDefaultDashboardPath(primaryRole) {
  switch (primaryRole) {
    case ROLE_CODES.ADMIN:
    case ROLE_CODES.DH:
      return '/admin/dashboard';
    case ROLE_CODES.CSG:
      return '/csg/approved-timesheets';
    case ROLE_CODES.PM:
      return '/pm/dashboard';
    case ROLE_CODES.RM:
      return '/pm/review-timesheets';
    default:
      return '/employee';
  }
}

/** Resolve primary role from API user or compute from flags. */
export function resolvePrimaryRole(user) {
  if (!user) return ROLE_CODES.EMPLOYEE;
  if (user.primaryRole) return user.primaryRole;
  return getPrimaryRole(user, {
    isReportingManager: user.isReportingManager,
    hasCsgProjects: user.hasCsgProjects,
  });
}

export function resolveAssignedRoles(user) {
  if (!user) return [ROLE_CODES.EMPLOYEE];
  if (user.assignedRoles?.length) return user.assignedRoles;
  return getAssignedRoles(user, {
    isReportingManager: user.isReportingManager,
    hasCsgProjects: user.hasCsgProjects,
  });
}

/** Routes that any active user may open to manage their own skill matrix. */
export const MY_SKILL_MATRIX_ROUTE_ROLES = Object.freeze([
  'employee',
  'admin',
  'deliveryHead',
  'projectManager',
  'reportingManager',
  'csg',
]);

export function isActiveUser(user) {
  if (!user) return false;
  if (user.isActive === false || user.isActive === 0) return false;
  if (user.is_active === false || user.is_active === 0) return false;
  return true;
}

export function canAccessMySkillMatrix(user) {
  if (!isActiveUser(user)) return false;
  return routeAllowsRole(MY_SKILL_MATRIX_ROUTE_ROLES, user);
}

/** Resource Finder: Admin, Delivery Head, PM, or CSG. */
export const RESOURCE_FINDER_ROUTE_ROLES = Object.freeze([
  'admin',
  'deliveryHead',
  'projectManager',
  'csg',
]);

export function canAccessResourceFinder(user) {
  if (!isActiveUser(user)) return false;
  return routeAllowsRole(RESOURCE_FINDER_ROUTE_ROLES, user);
}

/** Bench Management: Admin or Delivery Head. */
export function canAccessBenchManagement(user) {
  if (!isActiveUser(user)) return false;
  return routeAllowsRole(['admin', 'deliveryHead'], user);
}

/** Bench Analysis: full Admin only. */
export function canAccessBenchAnalysis(user) {
  if (!isActiveUser(user)) return false;
  return routeAllowsRole(['adminOnly'], user);
}

/** Team Resumes hierarchy browser: Admin, DH, PM, or RM. */
export const TEAM_RESUMES_ROUTE_ROLES = Object.freeze([
  'admin',
  'deliveryHead',
  'projectManager',
  'reportingManager',
]);

export function canAccessTeamResumes(user) {
  if (!isActiveUser(user)) return false;
  return routeAllowsRole(TEAM_RESUMES_ROUTE_ROLES, user);
}

export function isMySkillMatrixRouteRoles(allowedRoles) {
  if (!allowedRoles || allowedRoles.length !== MY_SKILL_MATRIX_ROUTE_ROLES.length) {
    return false;
  }
  return MY_SKILL_MATRIX_ROUTE_ROLES.every((role) => allowedRoles.includes(role));
}

export function routeAllowsRole(allowedRoles, user) {
  const assigned = resolveAssignedRoles(user);
  return allowedRoles.some((r) => {
    if (r === 'adminOnly') return assigned.includes(ROLE_CODES.ADMIN);
    if (r === 'admin') return assigned.includes(ROLE_CODES.ADMIN) || assigned.includes(ROLE_CODES.DH);
    if (r === 'deliveryHead') return assigned.includes(ROLE_CODES.DH);
    if (r === 'projectManager') return assigned.includes(ROLE_CODES.PM);
    if (r === 'reportingManager') return assigned.includes(ROLE_CODES.RM);
    if (r === 'csg') return assigned.includes(ROLE_CODES.CSG) || isCsgFlag(user);
    if (r === 'employee') {
      return (
        !assigned.includes(ROLE_CODES.ADMIN) &&
        !assigned.includes(ROLE_CODES.DH) &&
        !assigned.includes(ROLE_CODES.PM)
      );
    }
    return false;
  });
}
